import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Download, Copy, CheckCircle2, AlertCircle, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const card = "rounded-2xl border border-sand-300 bg-white p-5";
export const h2 = "font-display font-semibold text-forest-700";
export const th = "text-left text-xs uppercase tracking-wider text-muted-foreground px-3 py-2";
export const td = "px-3 py-2 text-sm border-t border-sand-300";
export const field =
  "w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/40 focus:border-forest-600";

export function Bar({ percent }) {
  const p = Math.max(0, Math.min(100, percent || 0));
  return (
    <div className="h-2 rounded-full bg-sand-300 overflow-hidden" role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full bg-forest-600" style={{ width: `${p}%` }} />
    </div>
  );
}

export function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-auto p-6`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display font-semibold text-xl text-forest-700">{title}</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="p-1 text-foreground/60"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

// Shown ONCE after accounts are created or a password is reset — the passwords can't be read again.
export function CredentialsModal({ title, rows, onClose, note }) {
  const cols = [["name", "Name"], ["username", "Username"], ["email", "Email"], ["class_name", "Class"], ["section", "Section"], ["password", "Temporary password"]]
    .filter(([k]) => rows.some((r) => r[k]));
  const csv = () => [cols.map(([, l]) => csvCell(l)).join(","), ...rows.map((r) => cols.map(([k]) => csvCell(r[k])).join(","))].join("\n");
  const download = () => {
    const url = URL.createObjectURL(new Blob([csv()], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "credentials.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const copy = () => navigator.clipboard?.writeText(csv().replace(/","/g, "\t").replace(/"/g, "")).then(() => toast.success("Copied"));
  return (
    <Modal title={title} onClose={onClose} wide>
      <p className="text-sm text-amber-800 bg-gold-500/10 rounded-xl px-3 py-2" data-testid="credentials-warning">
        Save these now — the passwords are shown only once. Students sign in with their username at the Dashboard login page.
      </p>
      {note && <p className="mt-2 text-sm text-foreground/80" data-testid="credentials-note">{note}</p>}
      <div className="mt-3 overflow-x-auto rounded-xl border border-sand-300">
        <table className="w-full" data-testid="credentials-table">
          <thead><tr>{cols.map(([k, l]) => <th key={k} className={th}>{l}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i}>{cols.map(([k]) => <td key={k} className={`${td} ${k === "password" ? "font-mono" : ""}`}>{r[k] || "—"}</td>)}</tr>)}</tbody>
        </table>
      </div>
      <div className="mt-4 flex gap-2 justify-end">
        <button type="button" className="btn-ghost" onClick={copy}><Copy className="h-4 w-4" /> Copy</button>
        <button type="button" className="btn-primary" onClick={download}><Download className="h-4 w-4" /> Download CSV</button>
      </div>
    </Modal>
  );
}


// Country code + number. Type a code ("971", "+44") or a country name ("pak") and pick from the matches;
// pasting a full international number ("+971501234567") fills both parts.
// onCode(code, country, explicit): `country` is the entry that was picked (many countries share a code, e.g. +1);
// `explicit` is false when the code was only inferred from a pasted number. preferName = a country the caller already shows
// elsewhere: when it shares the code, it is the one displayed.
export function PhoneInput({ countries, code, onCode, number, onNumber, maxLength = 15, inputClass = field, testId = "phone", preferName = "" }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);
  const chosen = countries.find((c) => c.calling_code === code && c.name === preferName) || countries.find((c) => c.calling_code === code);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, "");
    const all = countries.filter((c) => !q || c.name.toLowerCase().includes(q) || c.calling_code.replace("+", "").startsWith(q));
    // codes that start with what was typed first, then names
    return all.sort((a, b) => Number(b.calling_code.replace("+", "").startsWith(q)) - Number(a.calling_code.replace("+", "").startsWith(q))).slice(0, 40);
  }, [countries, query]);
  const pick = (c) => { onCode(c.calling_code, c, true); setQuery(""); setOpen(false); };
  const typeNumber = (raw) => {
    if (raw.trim().startsWith("+")) {
      const digits = raw.replace(/\D/g, "");
      // longest calling code that the pasted number starts with
      const hit = countries.filter((c) => digits.startsWith(c.calling_code.replace("+", ""))).sort((a, b) => b.calling_code.length - a.calling_code.length)[0];
      if (hit) { onCode(hit.calling_code, hit, false); onNumber(digits.slice(hit.calling_code.length - 1).slice(0, maxLength)); return; }
    }
    onNumber(raw.replace(/\D/g, "").slice(0, maxLength));
  };
  return (
    <div className="flex gap-2" ref={box}>
      <div className="relative w-40 shrink-0">
        <input data-testid={`${testId}-code`} aria-label="Country code" role="combobox" aria-expanded={open} aria-controls={`${testId}-code-list`} autoComplete="off" required={!code}
          className={inputClass} placeholder="+ code or country"
          value={open ? query : chosen ? `${chosen.calling_code} ${chosen.name}` : code}
          onFocus={() => { setQuery(""); setOpen(true); }} onChange={(e) => { setQuery(e.target.value); setOpen(true); if (code) onCode(""); }}
          onKeyDown={(e) => { if (e.key === "Enter" && open && matches[0]) { e.preventDefault(); pick(matches[0]); } if (e.key === "Escape") setOpen(false); }} />
        {open && (
          <ul id={`${testId}-code-list`} role="listbox" className="absolute z-30 mt-1 max-h-56 w-64 overflow-auto rounded-xl border border-sand-300 bg-white shadow-lg text-sm">
            {matches.length === 0 && <li className="px-3 py-2 text-muted-foreground">No matching country</li>}
            {matches.map((c) => (
              <li key={c.name} role="option" aria-selected={c.calling_code === code} onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                className="flex justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-sand-100">
                <span>{c.name}</span><span className="text-foreground/60">{c.calling_code}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <input data-testid={`${testId}-number`} required inputMode="tel" maxLength={maxLength + 6} className={inputClass} placeholder="Mobile number"
        value={number} onChange={(e) => typeNumber(e.target.value)} autoComplete="tel-national" />
    </div>
  );
}


// The apps a school licence can include (id, name). Ticking all of them means "every app", including ones added later.
export const LICENSE_APPS = [
  ["quran", "Read Qur'an"], ["tajweed", "Tajweed"], ["understand", "Understand Qur'an"], ["qfc", "Quran Fluency"], ["words", "85% Quranic Words"],
  ["poems", "Children Poems"], ["stories", "600 Qur'anic Stories"], ["hifz", "Hifz with Hikmah"], ["lms", "Short Courses"],
];

// A NEW licence starts with only Poems and Stories ticked; the admin ticks anything else the school should get.
export const DEFAULT_LICENSE_APPS = ["poems", "stories"];

// Checkbox list for choosing which apps a school gets. `value` is the list of chosen ids.
export function AppPicker({ value, onChange }) {
  const all = value.length === LICENSE_APPS.length;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <fieldset className="rounded-xl border border-sand-300 p-3" data-testid="app-picker">
      <legend className="px-1 text-xs font-semibold text-forest-700">Apps this school gets</legend>
      <label className="flex items-center gap-2 text-sm font-semibold mb-2">
        <input type="checkbox" checked={all} onChange={() => onChange(all ? [] : LICENSE_APPS.map(([id]) => id))} data-testid="app-all" /> All apps
      </label>
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
        {LICENSE_APPS.map(([id, name]) => (
          <label key={id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={value.includes(id)} onChange={() => toggle(id)} data-testid={`app-${id}`} /> {name}</label>))}
      </div>
    </fieldset>);
}


// Live "is there an account with this e-mail?" feedback for the change-e-mail dialogs. `fetcher(email)` resolves to
// { state: invalid | same | none | found | blocked, name?, message? }; the answer is shown as a green tick with the person's name.
export function useEmailLookup(fetcher, email) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = useRef(fetcher);
  run.current = fetcher;
  useEffect(() => {
    const e = (email || "").trim();
    setInfo(null);
    if (!e) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      run.current(e).then((r) => { if (alive) { setInfo(r); setLoading(false); } }).catch(() => { if (alive) { setInfo(null); setLoading(false); } });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [email]);
  return { info, loading, blocked: !!info && info.state === "blocked" };
}

export function EmailLookupNote({ info, loading }) {
  if (loading) return <div className="flex items-center gap-2 text-sm text-foreground/60" data-testid="email-lookup-loading"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</div>;
  if (!info || info.state === "invalid") return null;
  if (info.state === "found") return (
    <div className="flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-sm text-emerald-800" data-testid="email-lookup-found">
      <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" /><div><span className="font-semibold">User found</span>: {info.name}{info.detail && <div className="mt-0.5 text-xs text-emerald-700">{info.detail}</div>}</div></div>);
  if (info.state === "blocked") return (
    <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-800" data-testid="email-lookup-blocked">
      <AlertCircle className="h-5 w-5 shrink-0 text-red-600" /><div>{info.name && <span className="font-semibold">{info.name}: </span>}{info.message}</div></div>);
  return (
    <div className="flex items-start gap-2 rounded-xl bg-sand-100 px-3 py-2.5 text-sm text-foreground/70" data-testid="email-lookup-none">
      <Info className="h-5 w-5 shrink-0 text-foreground/50" /><div>{info.message}</div></div>);
}
